import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBookings } from '../../../Redux/Dashboard/External/TenentBooking/BookingsAction';
import { bookTenant, resetOnSuccessBookingTenant } from '../../../Redux/Dashboard/External/TenentBooking/TenentBookingAction'
import { releaseTenant, resetOnSuccessReleaseTenant } from '../../../Redux/Dashboard/External/TenentBooking/ReleaseTenentAction';
import Navbar from "../../../ReusableBlocks/Navbar";
import Swal from 'sweetalert2';
import 'react-tooltip/dist/react-tooltip.css'
import LoadingScreen from '../../../ReusableBlocks/LoadingScreen';
import { useSnackbar } from 'notistack';
import 'react-toastify/dist/ReactToastify.css';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import FilterSortBar from "../../../ReusableBlocks/FilterSortBar";
import { partnerRoleService } from '../PartnerPortal/Services/partnerRoleService';
import {
  FiCheck,
  FiAlertTriangle,
  FiClock,
  FiRefreshCw,
  FiCopy,
  FiLink,
  FiLock,
  FiUser,
  FiSettings,
  FiKey,
  FiUsers,
  FiClipboard,
  FiActivity,
  FiBarChart2,
  FiLayers,
} from "react-icons/fi";

const hasConfiguredCredentialValue = (value) =>
  value != null && String(value).trim() !== "";

const hasAnyConfiguredCredential = (obj, keys) =>
  keys.some((key) => hasConfiguredCredentialValue(obj?.[key]));

const normalizeFeatureList = (value) => {
  if (Array.isArray(value)) {
    return [...new Set(value.map((feature) => String(feature).trim()).filter(Boolean))];
  }
  if (typeof value === "string" && value.trim()) {
    return [value.trim()];
  }
  return [];
};

const normalizeTenantFeatures = (value) => normalizeFeatureList(value);
const normalizeVersionFeatures = (value) => normalizeFeatureList(value);

const EMPTY_BOOKING_FEATURES = { tenantFeatures: [], versionFeatures: [] };

const renderFeatureChips = (features, listId, chipVariant, staggerStart = 0) => (
  features.map((feature, index) => (
    <li
      key={`${listId}-${feature}-${index}`}
      className={`booking-feature-cap__chip booking-feature-cap__chip--${chipVariant}`}
      style={{ "--feature-index": staggerStart + index }}
    >
      <FiCheck className="booking-feature-cap__chip-icon" aria-hidden />
      <span className="booking-feature-cap__chip-text">{feature}</span>
    </li>
  ))
);

const DRAWER_FEATURE_COMPANION_MIN_WIDTH = 1200;

const useWideDrawerFeatureCompanion = () => {
  const [isWide, setIsWide] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(min-width: ${DRAWER_FEATURE_COMPANION_MIN_WIDTH}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mediaQuery = window.matchMedia(`(min-width: ${DRAWER_FEATURE_COMPANION_MIN_WIDTH}px)`);
    const handleChange = (event) => setIsWide(event.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return isWide;
};

const KeyTenantFeaturesSummary = ({
  versionFeatures = [],
  tenantFeatures = [],
  idPrefix = "booking",
  className = "",
}) => {
  const included = versionFeatures || [];
  const additional = tenantFeatures || [];
  const totalCount = included.length + additional.length;

  if (totalCount === 0) return null;

  const includedListId = `${idPrefix}-included-features`;
  const additionalListId = `${idPrefix}-additional-features`;

  return (
    <section
      className={`booking-feature-cap booking-feature-cap--unified ${className}`.trim()}
      aria-label="Key Tenant Features"
    >
      <header className="booking-feature-cap__header">
        <span className="booking-feature-cap__icon" aria-hidden>
          <FiLayers />
        </span>
        <div className="booking-feature-cap__headings">
          <h3 className="booking-feature-cap__title">Key Tenant Features</h3>
        </div>
      </header>

      <div className="booking-feature-cap__content">
        {included.length > 0 ? (
          <div className="booking-feature-cap__subsection booking-feature-cap__subsection--included">
            <h4 className="booking-feature-cap__subsection-title">Included Features</h4>
            <ul
              id={includedListId}
              className="booking-feature-cap__chips"
              role="list"
            >
              {renderFeatureChips(included, includedListId, "included", 0)}
            </ul>
          </div>
        ) : null}

        {additional.length > 0 ? (
          <div className="booking-feature-cap__subsection booking-feature-cap__subsection--additional">
            <h4 className="booking-feature-cap__subsection-title">Additional Features</h4>
            <ul
              id={additionalListId}
              className="booking-feature-cap__chips"
              role="list"
            >
              {renderFeatureChips(additional, additionalListId, "additional", 0)}
            </ul>
          </div>
        ) : null}
      </div>
    </section>
  );
};

const RightSideModal = ({ isOpen, onClose, tenantDetails, isAdmin, currentUser }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const wideDrawerFeatureCompanion = useWideDrawerFeatureCompanion();

  const isLoadingPro = useSelector((state) => state.ReleaseTenent?.releasing);
  const errorMessagePro = useSelector((state) => state.ReleaseTenent?.releaseError);
  const updateSuccessPro = useSelector((state) => state.ReleaseTenent?.releaseSuccess);

  // -------------------------
  // Helpers
  // -------------------------
  const norm = (s = "") => String(s).trim().replace(/\s+/g, " ").toLowerCase();

  // -------------------------
  // Normalize input shape
  // tenantDetails can be:
  // 1) { tenantNumber, tenant: bookingObject }
  // 2) { tenantNumber, tenant: { value: bookingObject, labels: [] } }
  // 3) bookingObject directly (rare)
  // -------------------------
  const rawTenant = tenantDetails?.tenant ?? tenantDetails ?? {};
  const value = rawTenant?.value ?? rawTenant ?? {};

  const tenantNumber = Number(tenantDetails?.tenantNumber ?? value?.tenantNumber);
  const bookingEndTime = value?.bookingEndTime;
  const bookingTenantType = value?.bookingTenantType;

  // -------------------------
  // Ownership & permissions
  // -------------------------
  const currentUserFullName = `${currentUser?.firstname || ""} ${currentUser?.lastname || ""}`.trim();

  // backend sometimes marks only the caller's booking as "booked by user"
  const isMineByState = value?.state === "booked by user";

  // fallback name match (for admin bookings too)
  const isMineByName =
    norm(value?.bookedBy) && norm(currentUserFullName)
      ? norm(value.bookedBy) === norm(currentUserFullName)
      : false;

  const isMine = isMineByState || isMineByName;

  // release only for owner
  const canRelease = isMine;

  // credentials visible to admin OR owner
  const canViewSecrets = isAdmin || isMine;

  const hasDetails = useMemo(() => value && Object.keys(value).length > 0, [value]);

  const drawerTenantFeatures = useMemo(
    () => normalizeTenantFeatures(tenantDetails?.tenantFeatures),
    [tenantDetails]
  );

  const drawerVersionFeatures = useMemo(
    () => normalizeVersionFeatures(tenantDetails?.versionFeatures),
    [tenantDetails]
  );

  const hasDrawerFeatures = drawerTenantFeatures.length > 0 || drawerVersionFeatures.length > 0;
  const showCompanionFeatureSheet = isOpen && hasDrawerFeatures && wideDrawerFeatureCompanion;
  const showInlineDrawerFeatures = hasDrawerFeatures && !wideDrawerFeatureCompanion;

  // -------------------------
  // Copy helper
  // -------------------------
  const copyToClipboard = async (text, label = "Value") => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(String(text));

      enqueueSnackbar(`${label} copied to clipboard`, {
        variant: "success",
        autoHideDuration: 1800,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });
    } catch (err) {
      enqueueSnackbar(`Failed to copy ${label}`, {
        variant: "error",
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });
    }
  };

  // -------------------------
  // Effects: Release status
  // -------------------------
  useEffect(() => {
    if (!errorMessagePro) return;

    dispatch(resetOnSuccessReleaseTenant());
    Swal.fire({
      title: "Error",
      text: errorMessagePro,
      icon: "error",
      confirmButtonText: "Retry",
      customClass: {
        title: "my-custom-title-of-pop-login",
        confirmButton: "my-custom-button-of-pop",
      },
    });
  }, [errorMessagePro, dispatch]);

  useEffect(() => {
    if (!updateSuccessPro) return;

    dispatch(resetOnSuccessReleaseTenant());
    Swal.fire({
      title: "Release Successful",
      text: `Tenant ${tenantNumber} has been released successfully.`,
      icon: "success",
      confirmButtonText: "Ok",
      allowOutsideClick: false,
      customClass: {
        title: "my-custom-title-of-pop",
        confirmButton: "my-custom-button-of-pop",
      },
    }).then((result) => {
      if (result.isConfirmed) window.location.reload();
    });
  }, [updateSuccessPro, dispatch, tenantNumber]);

  // -------------------------
  // Actions
  // -------------------------
  const relaseTenentClick = () => {
    if (!tenantNumber) return;
    dispatch(releaseTenant(tenantNumber, bookingEndTime, bookingTenantType));
  };

  // -------------------------
  // UI Helpers
  // -------------------------
  const CredentialRow = ({
    label,
    value: fieldValue,
    href,
    copyable = false,
    secret = false,
    icon = null,
  }) => {
    if (!fieldValue && !href) return null;

    const displayValue = href || fieldValue;

    return (
      <div className="cred-row">
        <div className="cred-row__label">
          {icon ? <span className="cred-row__icon">{icon}</span> : null}
          <span>{label}</span>
        </div>

        <div className="cred-row__control">
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="cred-row__link"
              title={href}
            >
              {href}
            </a>
          ) : (
            <input
              className="cred-row__input"
              value={fieldValue || ""}
              type={secret ? "password" : "text"}
              readOnly
            />
          )}

          {copyable && (
            <button
              type="button"
              className="cred-copy-btn"
              onClick={() => copyToClipboard(displayValue, label)}
              title={`Copy ${label}`}
            >
              <FiCopy />
            </button>
          )}
        </div>
      </div>
    );
  };

  const CredentialSection = ({ title, icon, iconTone, note, children }) => {
    const items = React.Children.toArray(children).filter(Boolean);
    if (!items.length) return null;

    return (
      <div className="cred-section">
        <div className="cred-section__header">
          <div className="cred-section__title-wrap">
            <span
              className={`cred-section__icon${
                iconTone ? ` cred-section__icon--${iconTone}` : ""
              }`}
            >
              {icon}
            </span>
            <h4 className="cred-section__title">{title}</h4>
          </div>
        </div>

        {note ? <div className="cred-section__note">{note}</div> : null}

        <div className="cred-section__body">{items}</div>
      </div>
    );
  };

  // -------------------------
  // Content
  // -------------------------
  let tenentDetailsForm = null;

  if (!hasDetails) {
    tenentDetailsForm = <p>Details Not Available</p>;
  } else {
    const isPreGA = value?.bookingTenantType === "Pre-GA";

    tenentDetailsForm = (
      <>
        {showInlineDrawerFeatures ? (
          <KeyTenantFeaturesSummary
            versionFeatures={drawerVersionFeatures}
            tenantFeatures={drawerTenantFeatures}
            idPrefix="drawer"
            className="booking-feature-cap--drawer-summary"
          />
        ) : null}

        <CredentialSection title="Booking Details" icon={<FiUser />}>
          <CredentialRow
            label="Booked By"
            value={value?.bookedBy}
            icon={<FiUser />}
          />

          {isAdmin && (value?.userCompany || value?.company) ? (
            <CredentialRow
              label="Company"
              value={value?.userCompany || value?.company}
              icon={<FiSettings />}
            />
          ) : null}
        </CredentialSection>

        {canViewSecrets &&
          hasAnyConfiguredCredential(value, ["tenantUrl", "username", "password"]) && (
          <CredentialSection title="Tenant Credentials" icon={<FiKey />} iconTone="tenant">
            <CredentialRow
              label="Tenant Url"
              href={value?.tenantUrl}
              copyable
              icon={<FiLink />}
            />
            <CredentialRow
              label="Tenant Username"
              value={value?.username}
              copyable
              icon={<FiUser />}
            />
            <CredentialRow
              label="Tenant Password"
              value={value?.password}
              secret
              copyable
              icon={<FiLock />}
            />
          </CredentialSection>
        )}

        {canViewSecrets && !isPreGA && (
          <>
            {hasAnyConfiguredCredential(value, ["chatopsUsername", "chatopsPassword"]) && (
              <CredentialSection title="Teams Credentials" icon={<FiUsers />} iconTone="teams">
                <CredentialRow
                  label="Teams Username"
                  value={value?.chatopsUsername}
                  copyable
                  icon={<FiUser />}
                />
                <CredentialRow
                  label="Teams Password"
                  value={value?.chatopsPassword}
                  secret
                  copyable
                  icon={<FiLock />}
                />
              </CredentialSection>
            )}

            {hasAnyConfiguredCredential(value, ["jiraUrl", "jiraUsername", "jiraPassword"]) && (
              <CredentialSection
                title="Jira Credentials"
                icon={<FiClipboard />}
                iconTone="jira"
                note={
                  value?.jiraUrl ? (
                    <div className="cred-inline-note">
                      Atlassian has mandated 2FA authentication for JIRA. If you're redirected to 2FA while logging in, use{" "}
                      <a
                        href="https://groups.google.com/g/demodesign"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Google Groups - Demo Design
                      </a>{" "}
                      to obtain the code.
                    </div>
                  ) : null
                }
              >
                <CredentialRow
                  label="Jira URL"
                  href={value?.jiraUrl}
                  copyable
                  icon={<FiLink />}
                />
                <CredentialRow
                  label="Jira Username"
                  value={value?.jiraUsername}
                  copyable
                  icon={<FiUser />}
                />
                <CredentialRow
                  label="Jira Password"
                  value={value?.jiraPassword}
                  secret
                  copyable
                  icon={<FiLock />}
                />
              </CredentialSection>
            )}

            {hasAnyConfiguredCredential(value, [
              "catchpointUrl",
              "catchpointUsername",
              "catchpointPassword",
            ]) && (
              <CredentialSection title="Catchpoint Credentials" icon={<FiActivity />} iconTone="catchpoint">
                <CredentialRow
                  label="Catchpoint URL"
                  href={value?.catchpointUrl}
                  copyable
                  icon={<FiLink />}
                />
                <CredentialRow
                  label="Catchpoint Username"
                  value={value?.catchpointUsername}
                  copyable
                  icon={<FiUser />}
                />
                <CredentialRow
                  label="Catchpoint Password"
                  value={value?.catchpointPassword}
                  secret
                  copyable
                  icon={<FiLock />}
                />
              </CredentialSection>
            )}

            {hasAnyConfiguredCredential(value, [
              "dynatraceHost",
              "dynatraceUsername",
              "dynatracePassword",
            ]) && (
              <CredentialSection title="Dynatrace Credentials" icon={<FiBarChart2 />} iconTone="dynatrace">
                <CredentialRow
                  label="Dynatrace Host"
                  href={value?.dynatraceHost}
                  copyable
                  icon={<FiLink />}
                />
                <CredentialRow
                  label="Dynatrace Username"
                  value={value?.dynatraceUsername}
                  copyable
                  icon={<FiUser />}
                />
                <CredentialRow
                  label="Dynatrace Password"
                  value={value?.dynatracePassword}
                  secret
                  copyable
                  icon={<FiLock />}
                />
              </CredentialSection>
            )}
          </>
        )}

        <div className="card-btn-sec-new-marg">
          {canRelease && (
            <button className="card-cat-btn1" onClick={relaseTenentClick}>
              Release Tenant
            </button>
          )}
          <button className="card-cat-btn2" onClick={onClose}>
            Close
          </button>
        </div>

        <br />
      </>
    );
  }

  // -------------------------
  // UI
  // -------------------------
  return (
    <>
      {isLoadingPro && <LoadingScreen text="Tenant Releasing In Progress" />}

      <div>
        {isOpen && <div className="overlay" onClick={onClose}></div>}

        <div
          className={`tenant-details-drawer-shell__track ${isOpen ? "open" : ""} ${
            showCompanionFeatureSheet ? "has-feature-companion" : ""
          }`.trim()}
        >
          {showCompanionFeatureSheet ? (
            <aside
              className={`tenant-details-feature-companion ${isOpen ? "is-visible" : ""}`}
              aria-label="Key Tenant Features"
            >
              <KeyTenantFeaturesSummary
                versionFeatures={drawerVersionFeatures}
                tenantFeatures={drawerTenantFeatures}
                idPrefix="drawer-companion"
                className="booking-feature-cap--companion-sheet"
              />
            </aside>
          ) : null}

          <div className={`right-side-modal-booking ${isOpen ? "open" : ""}`}>
            <div className="modal-content-2-booking">
              <div className="booking-modal-header">
                <div className="booking-modal-header__left">
                  <h2 className="booking-modal-title">
                    {tenantNumber ? `Tenant ${tenantNumber}` : "Details"}
                  </h2>
                </div>

                <button
                  type="button"
                  className="booking-modal-close"
                  onClick={onClose}
                  aria-label="Close details panel"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>

              <div className="booking-modal-divider" />

              <div className="booking-modal-body">
                {tenentDetailsForm}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};





// 1) Hash a string to a number (stable)
const hashString = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // 32-bit
  }
  return Math.abs(hash);
};

// 2) Convert hash -> HSL color (nice + readable)
const colorFromLabel = (label) => {
  const h = hashString(label) % 360;      // hue 0-359
  const s = 70;                           // saturation
  const l = 45;                           // lightness
  return `hsl(${h}, ${s}%, ${l}%)`;
};


const normalizeState = (state = "") => state.toLowerCase().trim();

const isBlockedState = (state = "") => {
  const s = normalizeState(state);
  return [
    "faulted",
    "pending clean up",
    "maintenance",
    "under upgrade",
  ].includes(s);
};





const TenantBookingPage = (props) => {





  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { bookings, loading, error } = useSelector((state) => state.GetTenant);
  const currentUser = useSelector(state => state.auth?.currentUser);
  const isAdminBookingUser = currentUser?.typeOfUser === 'Admin';
  const isPAEBookingUser = partnerRoleService.isPAERoleUser(currentUser);
  const useMultiTenantBookingFlow = isAdminBookingUser || isPAEBookingUser;

  const tenants = bookings['booking-tenants'] || {};
  const adminTenantExternal = bookings['external-booking-tenants'] || {};
  const adminTenantTechnical = bookings['technical-booking-tenants'] || {};
  const preGATenant = bookings['pre-ga-tenants'] || {};
  const preGATenantDesclaimer = bookings?.['pre-ga-booking-info'];



  const [modalOpen, setModalOpen] = useState(false);
  const [bookingPopup, setBookingPopup] = useState(false);
  const [selectedPurpose, setSelectedPurpose] = useState('');
  const [selectedDuration, setSelectedDuration] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [sfdcId, setSfdcId] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedBookingFeatures, setSelectedBookingFeatures] = useState(EMPTY_BOOKING_FEATURES);
  const [adminTenantType, setAdminTenantType] = useState('');
  const [tenantDetails, setTenantDetails] = useState(null);

  // const [filter, setFilter] = useState("all"); // all | available
  const [sortBy, setSortBy] = useState("tenant"); // tenant | availability | timeLeft
  // const [label, setLabel] = useState("all"); // optional if you still want label filter via dropdown
  const [filterValue, setFilterValue] = useState("all");
  // "all" | "available" | "label:<LabelName>"

  const [tenantType, setTenantType] = useState(null);

  const isLoading = useSelector(state => state.GetTenant?.loading);
  const errorMessage = useSelector(state => state.GetTenant?.error);

  const isLoadingPass = useSelector(state => state.BookTenent?.loading);
  const errorMessagePass = useSelector(state => state.BookTenent?.error);
  const updateSuccessPass = useSelector(state => state.BookTenent?.bookingSuccess);

  // put this near the top of TenantBookingPage
  const normalizeEntry = ([id, raw]) => {
    // available / empty cases
    if (!raw || raw === "") {
      return [id, { value: {}, labels: [], tenantFeatures: [], versionFeatures: [] }];
    }

    const isWrapped = (x) =>
      x &&
      typeof x === "object" &&
      ("value" in x || "labels" in x || "tenantFeatures" in x || "versionFeatures" in x);

    let labels = [];
    let tenantFeatures = [];
    let versionFeatures = [];
    let v = raw;

    // unwrap 1st level: { value, labels, tenantFeatures, versionFeatures }
    if (isWrapped(v)) {
      labels = Array.isArray(v.labels) ? v.labels : [];
      tenantFeatures = normalizeTenantFeatures(v.tenantFeatures);
      versionFeatures = normalizeVersionFeatures(v.versionFeatures);
      v = v.value ?? v;
    }

    // fix broken nested: value.value.value...
    while (v && typeof v === "object" && "value" in v && Object.keys(v).length <= 5) {
      if (!labels.length && Array.isArray(v.labels)) labels = v.labels;
      if (!tenantFeatures.length) tenantFeatures = normalizeTenantFeatures(v.tenantFeatures);
      if (!versionFeatures.length) versionFeatures = normalizeVersionFeatures(v.versionFeatures);
      v = v.value;
    }

    // if booking object itself has labels
    if (!labels.length && Array.isArray(v?.labels)) labels = v.labels;

    // remove labels/features from inside value (single source of truth on wrapper)
    const {
      labels: _dropLabels,
      tenantFeatures: _dropTenantFeatures,
      versionFeatures: _dropVersionFeatures,
      ...cleanValue
    } = (v && typeof v === "object") ? v : {};

    return [id, { value: cleanValue, labels, tenantFeatures, versionFeatures }];
  };


  const applyFilter = (entries) =>
    entries.filter(([_, t]) => {
      const isAvailable = Object.keys(t.value).length === 0;

      if (filterValue === "all") return true;
      if (filterValue === "available") return isAvailable;

      if (filterValue.startsWith("label:")) {
        const labelName = filterValue.replace("label:", "");
        return (t.labels || []).includes(labelName);
      }

      return true;
    });



  const sortEntries = (entries) => {
    const copy = [...entries];

    const stateRankForTimeLeft = (t) => {
      const s = (t?.value?.state || "").toLowerCase();

      // 1) booked tenants with end time
      if ((s === "booked" || s === "booked by user") && t?.value?.bookingEndTime) return 1;

      // 2) pending clean up
      if (s === "pending clean up") return 2;

      // 3) faulted
      if (s === "faulted") return 3;

      // others
      return 4;
    };

    const timeLeftHours = (t) => {
      // IMPORTANT: if no end time => Infinity (so they don't affect booked sorting)
      const end = t?.value?.bookingEndTime;
      if (!end) return Number.POSITIVE_INFINITY;
      const diff = new Date(end) - new Date();
      return Math.floor(diff / (1000 * 60 * 60));
    };

    return copy.sort((a, b) => {
      const ta = a[1];
      const tb = b[1];

      const aAvail = Object.keys(ta.value).length === 0;
      const bAvail = Object.keys(tb.value).length === 0;

      if (sortBy === "availability") {
        return (bAvail - aAvail); // available first
      }

      if (sortBy === "timeLeft") {
        const ra = stateRankForTimeLeft(ta);
        const rb = stateRankForTimeLeft(tb);

        if (ra !== rb) return ra - rb;

        // both booked-with-end-time => sort by smallest time left
        if (ra === 1) return timeLeftHours(ta) - timeLeftHours(tb);

        // keep stable ordering inside pending/faulted/others (by tenant number)
        return Number(a[0]) - Number(b[0]);
      }

      // tenant number
      return Number(a[0]) - Number(b[0]);
    });
  };




  useEffect(() => {
    return () => {
      // Dispatch action to clear the error when the component unmounts or modal closes
      dispatch(resetOnSuccessReleaseTenant());
      dispatch(resetOnSuccessBookingTenant());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchBookings());
  }, [dispatch]);


  // Profile Fetching Status
  useEffect(() => {
    if (errorMessage && errorMessage !== 'You have not completed mandatory Tech Central Training, please complete it.') {
      Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'Retry',
        customClass: {
          title: 'my-custom-title-of-pop-login',
          confirmButton: 'my-custom-button-of-pop',
        },
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.reload();
        }
      });
    }
  }, [errorMessage]);




  // Password Update Status
  useEffect(() => {
    if (errorMessagePass) {
      dispatch(resetOnSuccessBookingTenant())
      Swal.fire({
        title: 'Error',
        text: errorMessagePass,
        icon: 'error',
        confirmButtonText: 'Retry',
        customClass: {
          title: 'my-custom-title-of-pop-login',
          confirmButton: 'my-custom-button-of-pop',
        },

      });

    }
  }, [errorMessagePass]);
  useEffect(() => {
    if (updateSuccessPass) {
      dispatch(resetOnSuccessBookingTenant())
      Swal.fire({
        title: "Booking Successful",
        text: `Tenant ${selectedTenant} has been Booked successfully.`,
        icon: "Success",
        confirmButtonText: "Ok",
        allowOutsideClick: false,
        customClass: {
          title: 'my-custom-title-of-pop',
          confirmButton: 'my-custom-button-of-pop'
        }
      }).then((result) => {
        if (result.isConfirmed) {

          window.location.reload();
          handleCloseBookingPopup();
        }
      });
    }
  }, [updateSuccessPass]);


  const allLabelOptions = useMemo(() => {
    const set = new Set();

    const addFrom = (obj) => {
      Object.entries(obj || {}).forEach(([_, raw]) => {
        const [, t] = normalizeEntry([_, raw]);   // t = { value, labels }
        (t.labels || []).forEach((l) => set.add(l));
      });
    };

    addFrom(tenants);
    if (isAdminBookingUser) {
      addFrom(adminTenantExternal);
      addFrom(adminTenantTechnical);
      addFrom(preGATenant);
    }

    return ["all", ...Array.from(set).sort()];
  }, [tenants, adminTenantExternal, adminTenantTechnical, preGATenant, isAdminBookingUser]);


  const filterDropdownOptions = useMemo(() => {
    // labels collected already in your allLabelOptions (or use your Set logic)
    const labelsOnly = allLabelOptions.filter((x) => x !== "all"); // e.g. ["V14","GenAI",...]
    return [
      { value: "all", label: "All" },
      { value: "available", label: "Available" },
      ...labelsOnly.map((l) => ({ value: `label:${l}`, label: l })),
    ];
  }, [allLabelOptions]);

  const sortDropdownOptions = useMemo(() => ([
    { value: "tenant", label: "Tenant Number" },
    { value: "availability", label: "Availability" },
    { value: "timeLeft", label: "Time Left" },
  ]), []);




  // New
  const normTenants = Object.entries(tenants).map(normalizeEntry);
  const filteredTenants = sortEntries(applyFilter(normTenants));

  const normAdminExternal = Object.entries(adminTenantExternal).map(normalizeEntry);
  const filteredAdminExternalTenants = sortEntries(applyFilter(normAdminExternal));

  const normAdminTechnical = Object.entries(adminTenantTechnical).map(normalizeEntry);
  const filteredAdminTechnicalTenants = sortEntries(applyFilter(normAdminTechnical));

  const normPreGA = Object.entries(preGATenant).map(normalizeEntry);
  const filteredPreGATenants = sortEntries(applyFilter(normPreGA));




  function getTimeLeft(endTime) {
    const currentTime = new Date();
    const targetTime = new Date(endTime);

    const diffMs = targetTime - currentTime;

    if (diffMs <= 0) return "Expired";

    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0 && minutes > 0) {
      return `${hours} h ${minutes} min`;
    }

    if (hours > 0) {
      return `${hours} h`;
    }

    return `${minutes} min`;
  }






  const handleTenantClick = (tenantId, tenant, tenantType, bookingFeatures = EMPTY_BOOKING_FEATURES) => {
    // const value = asValue(tenant);

    // block faulted / pending cleanup
    if (isBlockedState(tenant.state)) return;

    const normalizedFeatures = {
      tenantFeatures: normalizeTenantFeatures(bookingFeatures.tenantFeatures),
      versionFeatures: normalizeVersionFeatures(bookingFeatures.versionFeatures),
    };

    if (Object.keys(tenant).length === 0) {
      // available
      setSelectedTenant(tenantId);
      setSelectedBookingFeatures(normalizedFeatures);
      setTenantDetails(tenant);            // modal uses .tenant.*, we pass value there later
      setTenantType(tenantType);
      handleShowBookingPopup();
    } else {
      // booked
      toggleModal();
      setTenantDetails({
        tenantNumber: tenantId,
        tenant,
        tenantFeatures: normalizedFeatures.tenantFeatures,
        versionFeatures: normalizedFeatures.versionFeatures,
      });
    }
  };





  const handleAdminTenantClick = (tenantId, tenantValue, type, bookingFeatures = EMPTY_BOOKING_FEATURES) => {
    const isAvailable = Object.keys(tenantValue).length === 0;
    const normalizedFeatures = {
      tenantFeatures: normalizeTenantFeatures(bookingFeatures.tenantFeatures),
      versionFeatures: normalizeVersionFeatures(bookingFeatures.versionFeatures),
    };

    if (isAvailable) {
      setSelectedTenant(tenantId);
      setSelectedBookingFeatures(normalizedFeatures);
      setAdminTenantType(type);
      handleShowBookingPopup();
    } else {
      setTenantDetails({
        tenantNumber: tenantId,
        tenant: { value: tenantValue },
        tenantFeatures: normalizedFeatures.tenantFeatures,
        versionFeatures: normalizedFeatures.versionFeatures,
      });
      toggleModal();
    }
  };





  const handleShowBookingPopup = () => {
    setBookingPopup(true);
  };

  const handleCloseBookingPopup = () => {
    setSelectedPurpose('');
    setSelectedDuration('');
    setSelectedBookingFeatures(EMPTY_BOOKING_FEATURES);
    setBookingPopup(false);
  };

  const handlePurposeChange = (event) => {
    setSelectedPurpose(event.target.value);
  };

  const handleDurationChange = (event) => {
    setSelectedDuration(event.target.value);
  };

  const handleCustomerNameChange = (event) => {
    setCustomerName(event.target.value);
  };

  const handleSfdcIdChange = (event) => {
    setSfdcId(event.target.value);
  };


  const handleBookTenant = async () => {
    const tenantNumber = parseInt(selectedTenant, 10);



    if (!selectedPurpose) {
      enqueueSnackbar('Please Choose Purpose!', { variant: 'warning' });
      return;
    }

    if (!selectedDuration) {
      enqueueSnackbar('Please Choose Duration', { variant: 'warning' });
      return;
    }



    if (selectedPurpose === 'Customer Demo' && (!customerName || !sfdcId)) {
      enqueueSnackbar('Please provide both Customer Name and SFDC ID for Customer Demo.', { variant: 'warning' });
      return;
    }


    let finalTenantType;
    if (tenantType === 'PreGA') {
      finalTenantType = 'Pre-GA';
    } else if (currentUser?.typeOfUser === 'Internal SE') {
      finalTenantType = 'Internal';
    } else if (currentUser?.typeOfUser === 'Technical User') {
      finalTenantType = 'Technical';
    } else if (currentUser?.typeOfUser === 'Leadership') {
      finalTenantType = 'Internal';
    }
    else if (currentUser?.typeOfUser === 'External SE') {
      finalTenantType = 'External';
    } else {
      finalTenantType = 'Internal';
    }

    const bookingDetails = {
      bookingDuration: parseInt(selectedDuration, 10),
      tenantNumber: parseInt(selectedTenant),
      tenantType: finalTenantType,
      customer: selectedPurpose === 'Customer Demo' ? customerName : '',
      sfdcId: selectedPurpose === 'Customer Demo' ? sfdcId : '',
    };

    //setIsLoading(true); 
    try {
      await dispatch(bookTenant(bookingDetails));

    } catch (error) {

    } finally {
      //setIsLoading(false);
    }
  };


  // Admin
  const handleAdminBookTenant = async (adminTenantType2) => {
    const tenantNumber = parseInt(selectedTenant, 10);



    if (!selectedPurpose) {
      enqueueSnackbar('Please Choose Purpose!', { variant: 'warning' });
      return;
    }

    if (!selectedDuration) {
      enqueueSnackbar('Please Choose Duration', { variant: 'warning' });
      return;
    }



    if (selectedPurpose === 'Customer Demo' && (!customerName || !sfdcId)) {
      enqueueSnackbar('Please provide both Customer Name and SFDC ID for Customer Demo.', { variant: 'warning' });
      return;
    }




    const bookingDetails = {
      bookingDuration: parseInt(selectedDuration, 10),
      tenantNumber: parseInt(selectedTenant),
      tenantType: adminTenantType2,
      customer: selectedPurpose === 'Customer Demo' ? customerName : '',
      sfdcId: selectedPurpose === 'Customer Demo' ? sfdcId : '',
    };

    //setIsLoading(true); 
    try {
      await dispatch(bookTenant(bookingDetails));

    } catch (error) {

    } finally {
      //setIsLoading(false);
    }
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };




  const formattedDisclaimer = preGATenantDesclaimer?.replace(/\n/g, '<br />');

  // --- Tag config (edit or extend anytime) ---
  const TAG_COLORS = {
    V14: "#F59E0B",
    DWP: "#06B6D4",
    "Service Ops": "#8B5CF6",
    GenAI: "#3B82F6",
    SRM: "#EF4444",
  };

  const getTagColor = (label) => TAG_COLORS[label] || colorFromLabel(label);



  // Single chip with colored dot
  const Tag = ({ label, color }) => (
    <span className="tenant-tag">
      <span className="tenant-tag-dot" style={{ background: color || "#9CA3AF" }} />
      {label}
    </span>
  );

  // Renders up to maxVisible chips then a “+N”
  const TagList = ({ tags, maxVisible = 3, tenant, currentUser }) => {
    const mine = isTenantMine(tenant, currentUser);

    const visible = tags.slice(0, maxVisible);
    const hidden = tags.slice(maxVisible);
    const hiddenCount = Math.max(0, hidden.length);

    if (!tags.length && !mine) {
      return <div className="tenant-tags-row tenant-tags-row--empty" />;
    }

    return (
      <div className="tenant-tags-row">
        {mine && (
          <span className="tenant-tag tenant-tag--mine">
            <span className="tenant-tag-dot tenant-tag-dot--mine" />
            Booked by me
          </span>
        )}

        {visible.map((t) => (
          <Tag key={t} label={t} color={getTagColor(t)} />
        ))}

        {hiddenCount > 0 && (
          <span
            className="tenant-tag tenant-tag--neutral"
            data-tooltip-id="tenant-tags-tooltip"
            data-tooltip-content={hidden.join(", ")}
          >
            +{hiddenCount}
          </span>
        )}
      </div>
    );
  };



  // Responsive max-visible tags
  const useTagCap = () => {
    const [cap, setCap] = React.useState(3); // desktop

    React.useEffect(() => {
      const m1200 = window.matchMedia('(max-width: 1200px)'); // tablets
      const m576 = window.matchMedia('(max-width: 576px)');  // phones

      const update = () => setCap(m576.matches ? 1 : m1200.matches ? 2 : 3);
      update();

      m1200.addEventListener('change', update);
      m576.addEventListener('change', update);
      return () => {
        m1200.removeEventListener('change', update);
        m576.removeEventListener('change', update);
      };
    }, []);

    return cap;
  };





  const tagCap = useTagCap();


  const newNormalizeState = (state = "") => String(state).toLowerCase().trim();

  const norm = (s = "") => String(s).trim().replace(/\s+/g, " ").toLowerCase();

  const isTenantMine = (tenant, currentUser) => {
    const fullName = `${currentUser?.firstname || ""} ${currentUser?.lastname || ""}`.trim();

    const isMineByState = tenant?.state === "booked by user";
    const isMineByName =
      norm(tenant?.bookedBy) && norm(fullName)
        ? norm(tenant.bookedBy) === norm(fullName)
        : false;

    return isMineByState || isMineByName;
  };

  const newIsEmptyTenant = (tenant) =>
    !tenant || (typeof tenant === "object" && Object.keys(tenant).length === 0);


  const getNewTenantBadgeIcon = (tenant = {}) => {
    if (newIsEmptyTenant(tenant)) return <FiCheck className="new-card-badge__icon" />;

    const s = newNormalizeState(tenant?.state);

    if (s === "booked by user") return <FiUser className="new-card-badge__icon" />;
    if (s === "booked") return <FiUser className="new-card-badge__icon" />;
    if (s === "faulted") return <FiAlertTriangle className="new-card-badge__icon" />;
    if (s === "pending clean up") return <FiClock className="new-card-badge__icon" />;
    if (s === "maintenance") return <FiSettings className="new-card-badge__icon" />;
    if (s === "upgrade") return <FiRefreshCw className="new-card-badge__icon" />;

    return <FiUser className="new-card-badge__icon" />;
  };

  const newIsBlockedState = (state = "") => {
    const s = newNormalizeState(state);
    return [
      "faulted",
      "pending clean up",
      "maintenance",
      "upgrade",
    ].includes(s);
  };

  const getNewTenantCardClass = (tenant = {}) => {
    if (newIsEmptyTenant(tenant)) return "new-tenant-card--available";

    const s = newNormalizeState(tenant?.state);

    if (s === "booked by user") return "new-tenant-card--booked";
    if (s === "booked") return "new-tenant-card--booked";
    if (s === "faulted") return "new-tenant-card--faulted";
    if (s === "pending clean up") return "new-tenant-card--pending-clean-up";
    if (s === "maintenance") return "new-tenant-card--maintenance";
    if (s === "upgrade") return "new-tenant-card--under-upgrade";

    return "new-tenant-card--booked";
  };

  const getNewTenantBadgeClass = (tenant = {}) => {
    if (newIsEmptyTenant(tenant)) return "new-tenant-badge--available";

    const s = newNormalizeState(tenant?.state);

    if (s === "booked by user") return "new-tenant-badge--booked-by-user";
    if (s === "booked") return "new-tenant-badge--booked";
    if (s === "faulted") return "new-tenant-badge--faulted";
    if (s === "pending clean up") return "new-tenant-badge--pending-clean-up";
    if (s === "maintenance") return "new-tenant-badge--maintenance";
    if (s === "upgrade") return "new-tenant-badge--under-upgrade";

    return "new-tenant-badge--booked";
  };

  const getNewTenantBadgeLabel = (tenant = {}) => {
    if (newIsEmptyTenant(tenant)) return "Available";

    const s = newNormalizeState(tenant?.state);

    if (s === "booked by user") return "Booked";
    if (s === "booked") return "Booked";
    if (s === "faulted") return "Faulted";
    if (s === "pending clean up") return "Pending Cleanup";
    if (s === "maintenance") return "Maintenance";
    if (s === "upgrade") return "Under Upgrade";

    return "Booked";
  };

  const getNewTenantClickable = (tenant = {}, isAdmin = false) => {
    if (isAdmin) return true;
    if (newIsEmptyTenant(tenant)) return true;
    return !newIsBlockedState(tenant?.state);
  };
  const NewCompanyPill = ({ name }) => {
    if (!name) return null;

    return (
      <span className="new-tenant-company-pill" title={name}>
        <span className="new-tenant-company-pill__text">{name}</span>
      </span>
    );
  };



  const NewBookedInfo = ({ by, company, showCompany, hours }) => {
    return (
      <div className="new-card-info new-card-info--booked">
        <div className="new-card-line">
          <span className="new-card-line__label">Booked By</span>
          <span className="new-card-line__colon">:</span>
          <span
            className="new-card-line__value new-card-line__value--ellipsis"
            title={by || "—"}
          >
            {by || "—"}
          </span>
        </div>

        {showCompany && company ? (
          <div className="new-card-line">
            <span className="new-card-line__label">Company</span>
            <span className="new-card-line__colon">:</span>
            <span
              className="new-card-line__value new-card-line__value--ellipsis"
              title={company}
            >
              {company}
            </span>
          </div>
        ) : null}

        <div className="new-card-line">
          <span className="new-card-line__label">Time Left</span>
          <span className="new-card-line__colon">:</span>
          <span className="new-card-line__value">{hours || "—"}</span>
        </div>
      </div>
    );
  };

  const NewFaultedInfo = ({ tenant, showCompany }) => {
    return (
      <div className="new-card-info new-card-info--faulted">
        <div className="new-card-line">
          <span className="new-card-line__label">Faulted By</span>
          <span className="new-card-line__colon">:</span>
          <span
            className="new-card-line__value new-card-line__value--ellipsis"
            title={tenant?.bookedBy || "—"}
          >
            {tenant?.bookedBy || "—"}
          </span>
        </div>

        {showCompany && tenant?.userCompany ? (
          <div className="new-card-line">
            <span className="new-card-line__label">Company</span>
            <span className="new-card-line__colon">:</span>
            <span
              className="new-card-line__value new-card-line__value--ellipsis"
              title={tenant.userCompany}
            >
              {tenant.userCompany}
            </span>
          </div>
        ) : null}
      </div>
    );
  };

  const NewPendingInfo = () => <div className="new-card-info new-card-info--empty" />;
  const NewMaintenanceInfo = () => <div className="new-card-info new-card-info--empty" />;
  const NewUpgradeInfo = () => <div className="new-card-info new-card-info--empty" />;

  const renderNewTenantContent = (tenant, { isAdmin, showCompanyPill }) => {
    if (newIsEmptyTenant(tenant)) {
      return <div className="new-card-info new-card-info--empty" />;
    }

    const s = newNormalizeState(tenant?.state);

    if (s === "faulted") {
      return (
        <NewFaultedInfo
          tenant={tenant}
          showCompany={showCompanyPill}
        />
      );
    }

    if (s === "pending clean up") return <NewPendingInfo />;
    if (s === "maintenance") return <NewMaintenanceInfo />;
    if (s === "upgrade") return <NewUpgradeInfo />;

    return (
      <NewBookedInfo
        by={isTenantMine(tenant, currentUser) ? "You" : tenant?.bookedBy}
        company={tenant?.userCompany}
        showCompany={showCompanyPill}
        hours={tenant?.bookingEndTime ? getTimeLeft(tenant.bookingEndTime) : null}
      />
    );
  };



  const renderTenantCard = (tenantId, t, tenantType, isAdmin = false) => {
    const tenant = t?.value || {};
    const tags = t?.labels || [];
    const bookingFeatures = {
      tenantFeatures: normalizeTenantFeatures(t?.tenantFeatures),
      versionFeatures: normalizeVersionFeatures(t?.versionFeatures),
    };
    const clickable = getNewTenantClickable(tenant, isAdmin);
    const showCompanyPill = isAdmin && tenantType === "External";


    const handleClick = () => {
      if (isAdmin) {
        handleAdminTenantClick(tenantId, tenant, tenantType, bookingFeatures);
        return;
      }

      if (!clickable) return;
      handleTenantClick(tenantId, tenant, tenantType, bookingFeatures);
    };

    return (
      <Col xs={12} sm={6} md={5} lg={4} key={tenantId} className="mb-4">
        <div
          className={`new-card ${getNewTenantCardClass(tenant)} ${isTenantMine(tenant, currentUser) ? "new-card--mine" : ""}`}
          onClick={handleClick}
          style={{ cursor: clickable ? "pointer" : "not-allowed" }}
        >
          <div className="new-card__body">
            <div className="new-card__top">
              <h3 className="new-card__title">
                <span>Tenant {tenantId}</span>

              </h3>

              <span className={`new-card-badge ${getNewTenantBadgeClass(tenant)}`}>
                {getNewTenantBadgeIcon(tenant)}
                <span>{getNewTenantBadgeLabel(tenant)}</span>
              </span>
            </div>

            {renderNewTenantContent(tenant, {
              isAdmin,
              showCompanyPill,
            })}

            <div className="new-card__bottom">
              <TagList
                tags={tags}
                maxVisible={tagCap}
                tooltipId={`tags-tenant-${tenantType}-${tenantId}`}
              />
            </div>
          </div>

        </div>
      </Col>
    );
  };

  const renderTenantSection = ({
    title,
    tenants,
    tenantType,
    isAdmin = false,
    showWarning = false,
  }) => {
    return (
      <>
        <p
          style={{
            fontWeight: "500",
            fontSize: "1.1rem",
            color: "#323232",
            marginBottom: "-10px",
          }}
        >
          {title}
        </p>
        <hr
          style={{
            border: "1px solid #757575",
            width: "30%",
            marginBottom: "1rem",
          }}
        />

        {showWarning ? (
          <div className="pre-ga-warning-container">
            <p className="pre-ga-warning-title">⚠️ Important Information</p>
            <div
              className="pre-ga-warning-text"
              dangerouslySetInnerHTML={{
                __html: formattedDisclaimer
                  ? formattedDisclaimer.replace(/\n/g, "<br />")
                  : "Disclaimer text is not available.",
              }}
            />
          </div>
        ) : null}

        <Row>
          {tenants.map(([tenantId, t]) =>
            renderTenantCard(tenantId, t, tenantType, isAdmin)
          )}
        </Row>
      </>
    );
  };





  return (
    <>
      {isLoading && <LoadingScreen text="Getting All Bookings" />}
      {isLoadingPass && <LoadingScreen text="Booking in Progress" />}

      <Navbar activeTab={"BOOKING"} />

      <div className="tenant-booking-background">
        <br />
        <Container>
          <Row className="mb-3">
            <Col xs={12} className="d-flex justify-content-end">
              <FilterSortBar
                filterValue={filterValue}
                filterOptions={filterDropdownOptions}
                onFilterChange={setFilterValue}
                sortValue={sortBy}
                sortOptions={sortDropdownOptions}
                onSortChange={setSortBy}
              />
            </Col>
          </Row>

          <br />

          {isAdminBookingUser ? (
            <>
              {renderTenantSection({
                title: "Internal Tenant",
                tenants: filteredTenants,
                tenantType: "Internal",
                isAdmin: true,
              })}

              {renderTenantSection({
                title: "Pre-GA Tenant",
                tenants: filteredPreGATenants,
                tenantType: "Pre-GA",
                isAdmin: true,
                showWarning: true,
              })}

              {renderTenantSection({
                title: "External Tenant",
                tenants: filteredAdminExternalTenants,
                tenantType: "External",
                isAdmin: true,
              })}

              {renderTenantSection({
                title: "Technical Tenant",
                tenants: filteredAdminTechnicalTenants,
                tenantType: "Technical",
                isAdmin: true,
              })}
            </>
          ) : isPAEBookingUser ? (
            <>
              {renderTenantSection({
                title: "Internal Tenant",
                tenants: filteredTenants,
                tenantType: "Internal",
                isAdmin: true,
              })}
            </>
          ) : (
            <Row>
              {filteredTenants.map(([tenantId, t]) =>
                renderTenantCard(tenantId, t, "Internal", false)
              )}
            </Row>
          )}
        </Container>
      </div>

      {/* Booking Popup */}
      <Modal
        dialogClassName={`book-tenant-modal ${
          selectedBookingFeatures.tenantFeatures.length === 0
          && selectedBookingFeatures.versionFeatures.length === 0
            ? 'booking-modal--compact'
            : 'booking-modal--with-features'
        }`}
        aria-labelledby="book-tenant-modal-title"
        centered
        show={bookingPopup}
        onHide={handleCloseBookingPopup}
        fullscreen={false}
      >
        <Modal.Body className="book-tenant-modal__content">
          <div className="book-tenant-modal__header booking-modal-header">
            <div className="booking-modal-header__left">
              <h2 id="book-tenant-modal-title" className="booking-modal-title">
                Book Tenant {selectedTenant}
              </h2>
              <p className="book-tenant-modal__subtitle">
                Provide booking details to reserve this tenant.
              </p>
            </div>
            <button
              type="button"
              className="booking-modal-close"
              onClick={handleCloseBookingPopup}
              aria-label="Close booking modal"
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>

          <div className="booking-modal-divider" />

          <div className="book-tenant-modal__body">
            <div className="book-tenant-modal__grid">
              {(selectedBookingFeatures.versionFeatures.length > 0
                || selectedBookingFeatures.tenantFeatures.length > 0) ? (
                <div className="book-tenant-modal__context">
                  <KeyTenantFeaturesSummary
                    versionFeatures={selectedBookingFeatures.versionFeatures}
                    tenantFeatures={selectedBookingFeatures.tenantFeatures}
                    idPrefix="book-tenant"
                  />
                </div>
              ) : null}

              <div className="book-tenant-modal__form">
                <h3 className="book-tenant-modal__section-title">Booking details</h3>
                <form className="book-tenant-modal__form-fields">
                  <div className="form-group">
                    <label>Purpose:</label>
                    <select className="custom-dropdown-tenant" value={selectedPurpose} onChange={handlePurposeChange}>
                      <option value="">Select Purpose</option>
                      <option value="Customer Demo">Customer Demo</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  {selectedPurpose === 'Customer Demo' && (
                    <>
                      <div className="form-group">
                        <label>Customer Name:</label>
                        <input
                          type="text"
                          className="form-control"
                          value={customerName}
                          onChange={handleCustomerNameChange}
                        />
                      </div>
                      <div className="form-group">
                        <label>SFDC ID:</label>
                        <input
                          type="text"
                          className="form-control"
                          value={sfdcId}
                          onChange={handleSfdcIdChange}
                        />
                      </div>
                    </>
                  )}
                  <div className="form-group">
                    <label>Duration (in hours):</label>
                    <select
                      className="custom-dropdown-tenant"
                      value={selectedDuration}
                      onChange={handleDurationChange}
                    >
                      <option value="">Select Duration</option>
                      <option value="4">4 Hours</option>
                      <option value="8">8 Hours</option>
                      <option value="12">12 Hours</option>
                    </select>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="book-tenant-modal__footer">
            <button
              type="button"
              className="book-tenant-modal__cancel"
              onClick={handleCloseBookingPopup}
            >
              Cancel
            </button>
            {useMultiTenantBookingFlow ? (
              <button className="tenant-book-btn" onClick={() => handleAdminBookTenant(adminTenantType)}>
                Book Tenant
              </button>
            ) : (
              <button className="tenant-book-btn" onClick={handleBookTenant}>
                Book Tenant
              </button>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <RightSideModal
        isOpen={modalOpen}
        onClose={toggleModal}
        tenantDetails={tenantDetails}
        isAdmin={useMultiTenantBookingFlow}
        currentUser={currentUser}
      />

      <ReactTooltip id="tenant-tags-tooltip" place="top" />
    </>
  );
};

export default TenantBookingPage;
